import React from 'react';

interface UiToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
  /** Track color when on — defaults to --theme-primary. Pass a CSS color or var. */
  onColor?: string;
  className?: string;
  ariaLabel?: string;
}

const SIZES = {
  sm: { track: 'h-5 w-9', knob: 'h-3.5 w-3.5', on: 'translate-x-4', off: 'translate-x-1' },
  md: { track: 'h-6 w-11', knob: 'h-4 w-4', on: 'translate-x-6', off: 'translate-x-1' },
} as const;

/**
 * Shared toggle switch primitive. On-state uses --theme-primary by default so
 * it stays on-theme; pass `onColor` for a semantic override.
 */
const UiToggle: React.FC<UiToggleProps> = ({
  checked,
  onChange,
  disabled = false,
  size = 'md',
  onColor = 'var(--theme-primary)',
  className = '',
  ariaLabel,
}) => {
  const s = SIZES[size];
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex ${s.track} items-center rounded-full transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${checked ? '' : 'bg-gray-300 dark:bg-slate-600'} ${className}`}
      style={checked ? { backgroundColor: onColor } : undefined}
    >
      <span
        className={`inline-block ${s.knob} transform rounded-full bg-white shadow transition-transform duration-200 ${checked ? s.on : s.off}`}
      />
    </button>
  );
};

export default UiToggle;
